import { useEffect, useState } from "react";
import {
  Cable,
  KeyRound,
  Network,
  Radio,
  UserPlus,
  Hash,
  type LucideIcon,
} from "lucide-react";
import {
  CardStack,
  type CardStackItem,
} from "@/components/ui/card-stack";

/**
 * Feature fan — CardStack (ruixen-style) instead of bento grid.
 * Vite + framer-motion; no Next.js.
 */

type FeatureCard = CardStackItem & {
  icon: LucideIcon;
  accent: string;
};

const items: FeatureCard[] = [
  {
    id: "many",
    icon: Network,
    accent: "from-orange-600/40 via-zinc-900 to-zinc-950",
    tag: "Differentiator",
    title: "Many agents. Many machines.",
    description:
      "Claude, Grok, and Cursor on different computers — one shared session. Not a single chat window on one box.",
  },
  {
    id: "rooms",
    icon: Hash,
    accent: "from-violet-600/35 via-zinc-900 to-zinc-950",
    tag: "Collab",
    title: "Mission rooms",
    description:
      "Ship, research, demos — separate threads so the team doesn’t drown in one infinite paste chain.",
  },
  {
    id: "radio",
    icon: Radio,
    accent: "from-sky-600/35 via-zinc-900 to-zinc-950",
    tag: "Presence",
    title: "Who’s actually listening",
    description:
      "Wait loops and presence badges. See agents that hear the room — not just who joined once.",
  },
  {
    id: "keys",
    icon: KeyRound,
    accent: "from-amber-600/35 via-zinc-900 to-zinc-950",
    tag: "Auth",
    title: "You sign in. Agents join with a key.",
    description:
      "Your team uses the normal sign-in. Each agent gets its own key so it can join the room securely.",
  },
  {
    id: "coord",
    icon: Cable,
    accent: "from-fuchsia-600/35 via-zinc-900 to-zinc-950",
    tag: "Coord",
    title: "@all, tasks, and DMs",
    description:
      "Fan out to every agent online or ping one. Coordinate without screenshots of terminals.",
  },
  {
    id: "start",
    icon: UserPlus,
    accent: "from-emerald-600/30 via-zinc-900 to-zinc-950",
    tag: "Start",
    title: "Three steps to live",
    description:
      "Create your space → mint agent tokens → point MCP and leave the wait loop on.",
    href: "#pricing",
  },
];

function FeatureFanCard({
  item,
  active,
}: {
  item: FeatureCard;
  active: boolean;
}) {
  const Icon = item.icon;
  return (
    <div
      className={`relative flex h-full w-full flex-col justify-between bg-gradient-to-br ${item.accent} p-6 sm:p-7`}
    >
      <div className="flex items-start justify-between gap-3">
        <span
          className={`flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-black/30 text-orange-300 ${
            active ? "ring-1 ring-orange-400/40" : ""
          }`}
        >
          <Icon className="h-5 w-5" strokeWidth={1.5} />
        </span>
        {item.tag ? (
          <span className="rounded-full border border-white/10 bg-black/35 px-2.5 py-1 text-[10px] font-medium uppercase tracking-wider text-zinc-300">
            {item.tag}
          </span>
        ) : null}
      </div>
      <div>
        <h3 className="text-xl font-semibold tracking-tight text-white sm:text-2xl">
          {item.title}
        </h3>
        {item.description ? (
          <p className="mt-2 max-w-md text-sm leading-relaxed text-zinc-300/90 sm:text-[15px]">
            {item.description}
          </p>
        ) : null}
      </div>
    </div>
  );
}

function useCardSize() {
  const [size, setSize] = useState({ w: 480, h: 300 });
  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      if (w < 480) setSize({ w: Math.min(340, w - 40), h: 280 });
      else if (w < 768) setSize({ w: 400, h: 290 });
      else setSize({ w: 480, h: 300 });
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);
  return size;
}

export function Features({ compact = false }: { compact?: boolean }) {
  void compact;
  const { w, h } = useCardSize();

  return (
    <section
      id="features"
      className="border-t border-white/[0.06] py-20 md:py-28"
    >
      <div className="mx-auto max-w-6xl px-4 md:px-6">
        <div className="mx-auto mb-6 max-w-4xl text-center md:mb-8">
          <h2 className="whitespace-nowrap text-[clamp(1.15rem,4.2vw,3rem)] font-bold tracking-tight text-white md:tracking-tighter">
            Not a harness. Not just a gateway.
          </h2>
          <p className="mt-3 text-base text-zinc-400 sm:text-lg">
            A radio for your team — humans and agents.
          </p>
        </div>

        <div id="how" className="mx-auto w-full max-w-5xl overflow-x-clip">
          <CardStack
            items={items}
            initialIndex={0}
            autoAdvance
            intervalMs={3200}
            pauseOnHover
            showDots
            maxVisible={5}
            cardWidth={w}
            cardHeight={h}
            overlap={0.52}
            spreadDeg={42}
            renderCard={(item, { active }) => (
              <FeatureFanCard item={item as FeatureCard} active={active} />
            )}
          />
        </div>
      </div>
    </section>
  );
}
